/**
 * Pricing Engine (Admin) - Excel workbook replacement in-app.
 *
 * Four tabs matching the build sequence:
 *   1. Name Map      - TVS invoice name ⇄ Amaravati package name (NAME_CONVERSION)
 *   2. Reference     - per-package inputs (unit cost, margin, GST%, HSN…)
 *   3. Retail Master - runs the calc + publishes to `packages` (GENERAL)
 *   4. AD Master     - same for AD branch type (sub-dealer bill)
 *
 * All backend calls hit `/api/pricing/*` (Admin-only `pricing.manage`).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Plus, PencilSimple, Trash, UploadSimple, Calculator, RocketLaunch, MagnifyingGlass } from "@phosphor-icons/react";
import { EvReferenceTab, EvMasterTab } from "./EvPricingTabs";

const inr = (v) => `₹${Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const TABS = [
  { k: "map",    label: "Name Map" },
  { k: "ref",    label: "Package Reference" },
  { k: "retail", label: "Retail Master" },
  { k: "ad",     label: "AD Master" },
  { k: "ev_ref",    label: "EV Reference", pill: "EV" },
  { k: "ev_retail", label: "EV Retail",    pill: "EV" },
  { k: "ev_ad",     label: "EV AD",        pill: "EV" },
];

export default function PricingPage() {
  const [tab, setTab] = useState("map");
  return (
    <div>
      <PageHeader
        eyebrow="Master"
        title="Pricing Engine"
        subtitle="Admin-only replacement for PRICE_LIST_MASTER.xlsx — map names, edit reference, compute, publish."
      />
      <div className="border-b border-canvas-line mb-6 flex gap-6 overflow-x-auto">
        {TABS.map((t) => (
          <button
            key={t.k}
            onClick={() => setTab(t.k)}
            data-testid={`pricing-tab-${t.k}`}
            className={`py-2 -mb-px border-b-2 whitespace-nowrap text-xs font-mono uppercase tracking-wider flex items-center gap-1.5 ${tab === t.k ? "border-ink text-ink font-semibold" : "border-transparent text-ink-muted hover:text-ink"}`}
          >
            {t.label}
            {t.pill && (
              <span className="px-1.5 py-0.5 bg-emerald-600 text-white text-[9px] tracking-widest">{t.pill}</span>
            )}
          </button>
        ))}
      </div>
      {tab === "map" && <NameMapTab />}
      {tab === "ref" && <ReferenceTab />}
      {tab === "retail" && <MasterTab branchTypeCode="GENERAL" />}
      {tab === "ad" && <MasterTab branchTypeCode="AD" />}
      {tab === "ev_ref" && <EvReferenceTab />}
      {tab === "ev_retail" && <EvMasterTab branchTypeCode="GENERAL" />}
      {tab === "ev_ad" && <EvMasterTab branchTypeCode="AD" />}
    </div>
  );
}

/* =============================================================
 * 1. NAME MAP
 * ============================================================= */
function NameMapTab() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [newTvs, setNewTvs] = useState("");
  const [newAma, setNewAma] = useState("");
  const [editing, setEditing] = useState(null);
  const [csvBusy, setCsvBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pricing/name-maps");
      setRows(data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    if (!s) return rows;
    return rows.filter((r) => (r.tvs_name + r.amaravati_name).toLowerCase().includes(s));
  }, [rows, search]);

  const add = async () => {
    if (!newTvs.trim() || !newAma.trim()) return;
    try {
      await api.post("/pricing/name-maps", { tvs_name: newTvs, amaravati_name: newAma });
      toast.success("Mapping added");
      setNewTvs(""); setNewAma("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/pricing/name-maps/${editing.id}`, {
        tvs_name: editing.tvs_name, amaravati_name: editing.amaravati_name,
      });
      toast.success("Mapping updated");
      setEditing(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (m) => {
    if (!window.confirm(`Remove mapping for "${m.tvs_name}"?`)) return;
    try {
      await api.delete(`/pricing/name-maps/${m.id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const importCsv = async (file) => {
    if (!file) return;
    setCsvBusy(true);
    try {
      const text = await file.text();
      const csv_b64 = btoa(unescape(encodeURIComponent(text)));
      const preview = await api.post("/pricing/name-maps/import", { csv_b64, dry_run: true });
      const p = preview.data;
      if (!window.confirm(`Import: ${p.inserted} new, ${p.updated} updated, ${p.skipped} skipped. Proceed?`)) return;
      const result = await api.post("/pricing/name-maps/import", { csv_b64, dry_run: false });
      toast.success(`Imported ${result.data.inserted + result.data.updated} rows`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setCsvBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[280px]">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search TVS or Amaravati name…"
            data-testid="namemap-search"
            className="w-full pl-8 pr-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink"
          />
        </div>
        <label className="inline-flex items-center gap-1.5 px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink cursor-pointer">
          <UploadSimple size={12} weight="bold" /> {csvBusy ? "…" : "Import CSV"}
          <input type="file" accept=".csv" className="hidden"
            onChange={(e) => { importCsv(e.target.files?.[0]); e.target.value = ""; }}
            data-testid="namemap-csv" />
        </label>
      </div>

      <div className="border border-canvas-line p-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[220px]">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">TVS invoice name</label>
          <input value={newTvs} onChange={(e) => setNewTvs(e.target.value)} data-testid="namemap-new-tvs"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm font-mono" placeholder="e.g. TVS JUPITER110 DRUM AL" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Amaravati package name</label>
          <input value={newAma} onChange={(e) => setNewAma(e.target.value)} data-testid="namemap-new-ama"
            className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" placeholder="e.g. TVS JUPITER 110-OBDIIA" />
        </div>
        <button onClick={add} data-testid="namemap-add"
          className="px-4 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1">
          <Plus size={12} weight="bold" /> Add
        </button>
      </div>

      <div className="border border-canvas-line overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
            <tr>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">TVS Name</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Amaravati Name</th>
              <th className="w-20"></th>
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={3} className="px-3 py-8 text-center text-ink-muted text-xs">No mappings. Import your NAME_CONVERSION CSV or add rows manually.</td></tr>
            ) : visible.map((m) => (
              <tr key={m.id} className="border-b border-canvas-line/50" data-testid={`namemap-row-${m.id}`}>
                <td className="px-3 py-2 font-mono text-xs">
                  {editing?.id === m.id ? (
                    <input value={editing.tvs_name} onChange={(e) => setEditing({ ...editing, tvs_name: e.target.value })}
                      className="w-full px-2 py-1 bg-canvas border border-canvas-line text-xs font-mono" />
                  ) : m.tvs_name}
                </td>
                <td className="px-3 py-2 text-sm">
                  {editing?.id === m.id ? (
                    <input value={editing.amaravati_name} onChange={(e) => setEditing({ ...editing, amaravati_name: e.target.value })}
                      className="w-full px-2 py-1 bg-canvas border border-canvas-line text-sm" />
                  ) : m.amaravati_name}
                </td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {editing?.id === m.id ? (
                    <>
                      <button onClick={saveEdit} className="text-emerald-700 text-xs font-mono uppercase px-2">Save</button>
                      <button onClick={() => setEditing(null)} className="text-ink-muted text-xs font-mono uppercase px-2">Cancel</button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => setEditing(m)} className="text-ink-muted hover:text-ink p-1" title="Edit"><PencilSimple size={12} /></button>
                      <button onClick={() => del(m)} className="text-rose-700 hover:bg-rose-50 p-1" title="Delete"><Trash size={12} /></button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =============================================================
 * 2. PACKAGE REFERENCE (form + list)
 * ============================================================= */
const REF_EMPTY = {
  package_name: "", branch_type_code: "GENERAL", model_category: "", active: true,
  unit_cost: 0, dealer_margin: 0, cc: 0, hsrp_amount: 325,
  helmet_landing: 360, helmet_sale_price: 929,
  fac: 1180, mandatory_acc_landing: 1035, mandatory_acc_margin: 568,
  life_tax: 0, reg_charges: 935, tr_charges: 0,
  idv_pct: 95, od_rate_pct: 1.676, nildip_pct: 0.4, life_tax_pct: 13.21,
  tp_under_150: 3851, tp_over_150: 7365,
  insured_value: 0, od_amount: 0, tp_value: 3851, pa: 525, nil_dip: 0,
  insurance_gst_pct: 18, insurance_fixed_charges: 300,
  add_acc_amount: 0, add_acc_margin: 0, helmet_margin: 400,
  full_accessories_amount: 0, ext_warranty_5y_amount: 0,
  ad_discount: 0, ad_discount_pct: 0, ad_mandatory_landing: 0, service_charge: 500, service_gst_pct: 18,
  company_discount: 0,
  package_hsn: "87112039", helmet_hsn: "87116020", accessories_hsn: "87116020",
  package_igst_pct: 9, package_cgst_pct: 9,
  helmet_igst_pct: 9, helmet_cgst_pct: 9,
  accessories_igst_pct: 9, accessories_cgst_pct: 9,
};

function ReferenceTab() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("active"); // active | inactive | deleted | all
  const [editing, setEditing] = useState(null);
  // Bulk-edit state
  const [bulkMode, setBulkMode] = useState(false);
  const [edits, setEdits] = useState({});      // rid -> { field: value }
  const [saving, setSaving] = useState(false);
  // Adjustment
  const [adjField, setAdjField] = useState("unit_cost");
  const [adjOp, setAdjOp] = useState("pct");
  const [adjValue, setAdjValue] = useState("");
  const [adjBusy, setAdjBusy] = useState(false);
  // Import
  const [importBusy, setImportBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/pricing/references", {
        params: {
          ...(branchFilter ? { branch_type_code: branchFilter } : {}),
          status: statusFilter,
        },
      });
      setRows(data || []);
      setEdits({});
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchFilter, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const s = search.trim().toLowerCase();
    return rows.filter((r) => {
      if (categoryFilter && (r.model_category || "").toUpperCase() !== categoryFilter) return false;
      if (s && !r.package_name.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [rows, search, categoryFilter]);

  const softDelete = async (r) => {
    if (!window.confirm(`Move "${r.package_name}" to the Deleted bin?\n\nIt'll be hidden from lists, blocked from publish, and excluded from bulk edits — but you can restore it anytime from the Deleted filter.`)) return;
    try {
      await api.delete(`/pricing/references/${r.id}`);
      toast.success("Moved to Deleted bin");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const restore = async (r) => {
    try {
      await api.post(`/pricing/references/${r.id}/restore`);
      toast.success("Reference restored");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const purge = async (r) => {
    if (!window.confirm(`⚠️ PERMANENTLY delete "${r.package_name}"?\n\nThis is irreversible — the row will be wiped from the database.`)) return;
    if (!window.confirm("Are you absolutely sure? Last chance.")) return;
    try {
      await api.delete(`/pricing/references/${r.id}/purge`);
      toast.success("Permanently deleted");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setCell = (rid, field, value) => {
    setEdits((e) => {
      const cur = { ...(e[rid] || {}) };
      cur[field] = value;
      return { ...e, [rid]: cur };
    });
  };

  const effective = (r, field) => {
    const v = edits[r.id]?.[field];
    return v === undefined || v === "" ? r[field] : v;
  };

  const dirtyCount = Object.keys(edits).filter((rid) =>
    Object.values(edits[rid] || {}).some((v) => v !== "" && v !== undefined)
  ).length;

  const saveBulk = async () => {
    const updates = Object.entries(edits)
      .map(([id, patch]) => {
        const clean = { id };
        for (const [k, v] of Object.entries(patch)) {
          if (v !== "" && v !== undefined && !Number.isNaN(Number(v))) {
            clean[k] = Number(v);
          }
        }
        return Object.keys(clean).length > 1 ? clean : null;
      })
      .filter(Boolean);
    if (updates.length === 0) { toast.error("No changes to save"); return; }
    if (!window.confirm(`Save changes to ${updates.length} row(s)?`)) return;
    setSaving(true);
    try {
      const { data } = await api.post("/pricing/references/bulk-update", { updates });
      toast.success(`Saved ${data.changed} row(s)${data.errors?.length ? ` (${data.errors.length} errors)` : ""}`);
      setBulkMode(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const runAdjust = async (dryRun) => {
    const v = parseFloat(adjValue);
    if (Number.isNaN(v)) { toast.error("Enter a numeric value"); return; }
    setAdjBusy(true);
    try {
      const ids = visible.map((r) => r.id);
      const { data } = await api.post("/pricing/references/adjust", {
        field: adjField, op: adjOp, value: v,
        filter: { ids }, dry_run: dryRun,
      });
      if (dryRun) {
        const sample = (data.preview || []).slice(0, 5).map((p) =>
          `${p.package_name}: ${p.before} → ${p.after}`).join("\n");
        if (!window.confirm(
          `Will change ${data.matched} row(s).\n\nPreview:\n${sample}${data.matched > 5 ? `\n… +${data.matched - 5} more` : ""}\n\nApply?`
        )) return;
        return runAdjust(false);
      }
      toast.success(`Adjusted ${data.changed} row(s)`);
      setAdjValue("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setAdjBusy(false); }
  };

  const doImport = async (file) => {
    if (!file) return;
    setImportBusy(true);
    try {
      const buf = await file.arrayBuffer();
      // Base64 encode
      let binary = "";
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
      const file_b64 = btoa(binary);
      // Preview
      const preview = await api.post("/pricing/references/import", {
        file_b64, filename: file.name, dry_run: true,
      });
      const p = preview.data;
      const willUpdate = (p.preview || []).filter((x) => x.status === "will_update").length;
      const createRows = (p.preview || []).filter((x) => x.status === "will_create");
      const unchanged = (p.preview || []).filter((x) => x.status === "unchanged").length;
      const createNames = createRows.map((x) => `  + ${x.package_name}`).join("\n");
      if (!window.confirm(
        `Rows read: ${p.total_rows}\nWill update: ${willUpdate}\nWill create (new models): ${createRows.length}${createRows.length ? `\n${createNames}` : ""}\nUnchanged: ${unchanged}\nMissing (not found): ${p.missing}\n\nProceed with import?`
      )) return;
      const result = await api.post("/pricing/references/import", {
        file_b64, filename: file.name, dry_run: false,
      });
      toast.success(`Imported — updated ${result.data.updated}, created ${result.data.created || 0}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setImportBusy(false); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlass size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-muted" />
          <input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search package name…" data-testid="ref-search"
            className="w-full pl-8 pr-3 py-2 bg-canvas border border-canvas-line text-sm focus:outline-none focus:border-ink" />
        </div>
        <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)} data-testid="ref-branch-filter"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm">
          <option value="">All branch types</option>
          <option value="GENERAL">GENERAL (Retail)</option>
          <option value="AD">AD (Sub-Dealer)</option>
        </select>
        <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} data-testid="ref-category-filter"
          className="px-3 py-2 bg-canvas border border-canvas-line text-sm">
          <option value="">All categories</option>
          {["MOPED", "SCOOTER", "MOTORCYCLE", "PREMIUM", "SUPER PREMIUM", "ELECTRIC"].map((c) =>
            <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setBulkMode(false); setEdits({}); }}
          data-testid="ref-status-filter"
          className={`px-3 py-2 border text-sm ${statusFilter === "deleted" ? "border-rose-400 bg-rose-50 text-rose-800" : "bg-canvas border-canvas-line"}`}>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="all">All (active + inactive)</option>
          <option value="deleted">🗑 Deleted bin</option>
        </select>
        <button
          onClick={async () => {
            try {
              const resp = await api.get("/pricing/references/export", { responseType: "blob" });
              const url = URL.createObjectURL(new Blob([resp.data]));
              const a = document.createElement("a");
              a.href = url;
              a.download = `pricing-master-${new Date().toISOString().slice(0, 10)}.xlsx`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
              toast.success("Exported pricing master workbook");
            } catch (e) { toast.error(formatApiError(e)); }
          }}
          data-testid="ref-export"
          className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink"
        >
          Export XLSX
        </button>
        <label className="px-3 py-2 border border-canvas-line text-xs font-mono uppercase tracking-wider hover:border-ink cursor-pointer flex items-center gap-1"
          data-testid="ref-import-btn">
          <UploadSimple size={12} weight="bold" />
          {importBusy ? "Importing…" : "Import XLSX"}
          <input type="file" accept=".xlsx,.xlsm,.csv" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doImport(f); }}
            disabled={importBusy} />
        </label>
        <button
          onClick={() => { setBulkMode((m) => !m); setEdits({}); }}
          disabled={statusFilter === "deleted"}
          data-testid="ref-bulk-toggle"
          className={`px-3 py-2 text-xs font-mono uppercase tracking-wider border ${bulkMode ? "bg-amber-600 text-white border-amber-600" : "border-canvas-line hover:border-ink"} disabled:opacity-40 disabled:cursor-not-allowed`}
        >
          {bulkMode ? "Exit Bulk Edit" : "Bulk Edit"}
        </button>
        {!bulkMode && statusFilter !== "deleted" && (
          <button onClick={() => setEditing({ ...REF_EMPTY })} data-testid="ref-add"
            className="px-3 py-2 bg-ink text-white text-xs font-mono uppercase tracking-wider hover:bg-brand flex items-center gap-1">
            <Plus size={12} weight="bold" /> New Reference
          </button>
        )}
      </div>

      {bulkMode && (
        <div className="border border-amber-300 bg-amber-50/40 p-3 space-y-2" data-testid="bulk-toolbar">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">
            Bulk adjustment — applies to the {visible.length} row(s) shown by the filters above
          </div>
          <div className="flex flex-wrap items-end gap-2 text-xs">
            <div>
              <label className="block font-mono text-[10px] uppercase text-ink-muted mb-1">Field</label>
              <select value={adjField} onChange={(e) => setAdjField(e.target.value)} data-testid="bulk-field"
                className="px-2 py-1 bg-canvas border border-canvas-line text-xs">
                <option value="unit_cost">Unit Cost</option>
                <option value="dealer_margin">Dealer Margin</option>
                <option value="mandatory_acc_landing">Mandatory Acc Landing</option>
                <option value="mandatory_acc_margin">Mandatory Acc Margin</option>
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase text-ink-muted mb-1">Operation</label>
              <select value={adjOp} onChange={(e) => setAdjOp(e.target.value)} data-testid="bulk-op"
                className="px-2 py-1 bg-canvas border border-canvas-line text-xs">
                <option value="pct">± Percent (%)</option>
                <option value="flat">± Flat (₹)</option>
              </select>
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase text-ink-muted mb-1">Value (+/-)</label>
              <input type="number" step="0.01" value={adjValue} onChange={(e) => setAdjValue(e.target.value)}
                placeholder={adjOp === "pct" ? "e.g. 2 for +2%" : "e.g. 500"}
                data-testid="bulk-value"
                className="px-2 py-1 bg-canvas border border-canvas-line text-xs w-32 font-mono" />
            </div>
            <button onClick={() => runAdjust(true)} disabled={adjBusy || !adjValue} data-testid="bulk-apply"
              className="px-3 py-1.5 bg-amber-600 text-white text-xs font-mono uppercase tracking-wider hover:bg-amber-700 disabled:opacity-50">
              {adjBusy ? "…" : "Preview & Apply"}
            </button>
            <div className="text-[10px] text-ink-muted font-mono ml-auto">
              Or edit cells inline below, then <span className="font-semibold text-amber-700">Save All</span>.
            </div>
          </div>
          {dirtyCount > 0 && (
            <div className="flex items-center justify-between pt-2 border-t border-amber-200">
              <div className="text-xs font-semibold text-amber-800">{dirtyCount} row(s) with unsaved edits</div>
              <div className="flex gap-2">
                <button onClick={() => setEdits({})} className="px-3 py-1 border border-canvas-line text-xs font-mono uppercase hover:border-ink">
                  Discard
                </button>
                <button onClick={saveBulk} disabled={saving} data-testid="bulk-save-all"
                  className="px-3 py-1 bg-emerald-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-emerald-800 disabled:opacity-50">
                  {saving ? "Saving…" : `Save All (${dirtyCount})`}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border border-canvas-line overflow-x-auto max-h-[60vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
            <tr>
              {(bulkMode
                ? ["Package Name", "Type", "Category", "Unit Cost", "Margin", "Man Landing", "Man Margin", ""]
                : ["Package Name", "Type", "Category", "Unit Cost", "Margin", "Life Tax", "Last Published", ""]
              ).map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[10px] uppercase text-left">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-8 text-center text-ink-muted text-xs">No references match filters.</td></tr>
            ) : visible.map((r) => {
              const isDirty = !!edits[r.id] && Object.values(edits[r.id]).some((v) => v !== "" && v !== undefined);
              return (
                <tr key={r.id}
                  className={`border-b border-canvas-line/50 ${isDirty ? "bg-amber-50/60" : ""}`}
                  data-testid={`ref-row-${r.id}`}>
                  <td className="px-3 py-2 text-sm font-semibold">
                    {r.package_name}
                    {r.active === false && (
                      <span className="ml-2 px-1.5 py-0.5 bg-ink-muted/20 text-ink-muted text-[9px] font-mono uppercase tracking-wider">Inactive</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs">{r.branch_type_code}</td>
                  <td className="px-3 py-2 text-xs">{r.model_category}</td>
                  {bulkMode ? (
                    <>
                      <BulkCell r={r} field="unit_cost" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="dealer_margin" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="mandatory_acc_landing" edits={edits} setCell={setCell} />
                      <BulkCell r={r} field="mandatory_acc_margin" edits={edits} setCell={setCell} />
                    </>
                  ) : (
                    <>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.unit_cost)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.dealer_margin)}</td>
                      <td className="px-3 py-2 font-mono text-xs">{inr(r.life_tax)}</td>
                      <td className="px-3 py-2 font-mono text-[10px]">
                        {r.last_published_at ? (
                          <>
                            <div>{inr(r.last_published_price)}</div>
                            <div className="text-ink-muted">{new Date(r.last_published_at).toLocaleString()}</div>
                          </>
                        ) : <span className="text-ink-muted">—</span>}
                      </td>
                    </>
                  )}
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {statusFilter === "deleted" ? (
                      <>
                        <button onClick={() => restore(r)} title="Restore"
                          data-testid={`ref-restore-${r.id}`}
                          className="text-emerald-700 hover:bg-emerald-50 p-1 text-xs font-mono uppercase">Restore</button>
                        <button onClick={() => purge(r)} title="Permanently delete"
                          data-testid={`ref-purge-${r.id}`}
                          className="text-rose-700 hover:bg-rose-50 p-1 text-xs font-mono uppercase">Purge</button>
                      </>
                    ) : !bulkMode && (
                      <>
                        <button onClick={() => setEditing(r)} className="text-ink-muted hover:text-ink p-1" title="Edit"><PencilSimple size={12} /></button>
                        <button onClick={() => softDelete(r)} data-testid={`ref-delete-${r.id}`} className="text-rose-700 hover:bg-rose-50 p-1" title="Move to Deleted bin"><Trash size={12} /></button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {editing && <ReferenceEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function BulkCell({ r, field, edits, setCell }) {
  const val = edits[r.id]?.[field];
  const hasEdit = val !== undefined && val !== "";
  return (
    <td className="px-2 py-1">
      <input
        type="number"
        step="0.01"
        value={hasEdit ? val : (r[field] ?? 0)}
        onChange={(e) => setCell(r.id, field, e.target.value)}
        data-testid={`bulk-cell-${r.id}-${field}`}
        className={`w-full px-2 py-1 text-xs font-mono border ${hasEdit ? "border-amber-500 bg-amber-50" : "border-canvas-line bg-canvas"}`}
      />
    </td>
  );
}

function ReferenceEditor({ initial, onClose, onSaved }) {
  const [form, setForm] = useState(initial);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const isNew = !initial.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const computePreview = async () => {
    try {
      const { data } = await api.post("/pricing/compute", { reference: form });
      setPreview(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const save = async () => {
    if (!form.package_name.trim()) { toast.error("Package name is required"); return; }
    if (!form.model_category.trim()) { toast.error("Model category is required"); return; }
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/pricing/references", form);
        toast.success(`Reference "${data.package_name}" created`);
      } else {
        await api.put(`/pricing/references/${initial.id}`, form);
        toast.success("Reference updated");
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="ref-editor">
      <div className="bg-canvas border border-canvas-line max-w-5xl w-full max-h-[92vh] overflow-y-auto">
        <div className="border-b border-canvas-line px-6 py-3 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">Package Reference</div>
            <div className="text-lg font-semibold">{isNew ? "New Reference" : form.package_name}</div>
          </div>
          <button onClick={onClose} className="text-ink-muted hover:text-ink text-xl leading-none">×</button>
        </div>

        <div className="p-6 grid grid-cols-1 lg:grid-cols-3 gap-3 text-sm">
          <Field label="Package name" required value={form.package_name} onChange={(v) => set("package_name", v)} testId="ref-name" />
          <SelectField label="Branch type" value={form.branch_type_code} onChange={(v) => set("branch_type_code", v)}
            options={["GENERAL", "AD"]} testId="ref-btc" />
          <div>
            <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">Model category *</label>
            <select value={form.model_category} onChange={(e) => set("model_category", e.target.value)} data-testid="ref-category"
              className={`w-full px-3 py-2 bg-canvas border text-sm ${!form.model_category ? "border-rose-400" : "border-canvas-line"}`}>
              <option value="">— Select category —</option>
              {["MOPED", "SCOOTER", "MOTORCYCLE", "PREMIUM", "SUPER PREMIUM", "ELECTRIC"].map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <label className="flex items-center gap-2 text-xs mt-6">
            <input type="checkbox" checked={form.active} onChange={(e) => set("active", e.target.checked)} /> Active
          </label>
          <div />

          <SectionTitle title="Vehicle Cost (TVS)" />
          <Num label="Unit cost (TVS ex-GST)" value={form.unit_cost} onChange={(v) => set("unit_cost", v)} testId="ref-unit-cost" />
          <Num label="Dealer margin ₹" value={form.dealer_margin} onChange={(v) => set("dealer_margin", v)} testId="ref-dealer-margin" />
          <Num label="CC" value={form.cc} onChange={(v) => set("cc", v)} />

          <SectionTitle title="On-Road Components" />
          <Num label="HSRP amount" value={form.hsrp_amount} onChange={(v) => set("hsrp_amount", v)} />
          <Num label="Life tax" value={form.life_tax} onChange={(v) => set("life_tax", v)} />
          <Num label="Registration" value={form.reg_charges} onChange={(v) => set("reg_charges", v)} />
          <Num label="TR charges" value={form.tr_charges} onChange={(v) => set("tr_charges", v)} />
          <Num label="Mandatory acc landing" value={form.mandatory_acc_landing} onChange={(v) => set("mandatory_acc_landing", v)} />
          <Num label="Mandatory acc margin" value={form.mandatory_acc_margin} onChange={(v) => set("mandatory_acc_margin", v)} />
          <Num label="FAC" value={form.fac} onChange={(v) => set("fac", v)} />
          <Num label="Helmet landing" value={form.helmet_landing} onChange={(v) => set("helmet_landing", v)} />
          <Num label="Helmet sale price" value={form.helmet_sale_price} onChange={(v) => set("helmet_sale_price", v)} />
          <Num label="Helmet margin" value={form.helmet_margin} onChange={(v) => set("helmet_margin", v)} />

          <SectionTitle title="Insurance + Life Tax (auto-derived from ex-showroom)" />
          <Num label="Life tax % of ex-showroom" value={form.life_tax_pct} onChange={(v) => set("life_tax_pct", v)} step="0.0001" />
          <Num label="IDV % of ex-showroom" value={form.idv_pct} onChange={(v) => set("idv_pct", v)} step="0.0001" />
          <Num label="OD premium % of IDV" value={form.od_rate_pct} onChange={(v) => set("od_rate_pct", v)} step="0.00001" />
          <Num label="Nil-Dip % of IDV" value={form.nildip_pct} onChange={(v) => set("nildip_pct", v)} step="0.00001" />
          <Num label="TP (CC ≤ 150)" value={form.tp_under_150} onChange={(v) => set("tp_under_150", v)} />
          <Num label="TP (CC > 150)" value={form.tp_over_150} onChange={(v) => set("tp_over_150", v)} />
          <Num label="PA cover ₹" value={form.pa} onChange={(v) => set("pa", v)} />
          <Num label="Insurance GST %" value={form.insurance_gst_pct} onChange={(v) => set("insurance_gst_pct", v)} step="0.1" />
          <Num label="Fixed charges (hypothecation)" value={form.insurance_fixed_charges} onChange={(v) => set("insurance_fixed_charges", v)} />
          <div className="lg:col-span-3 text-[10px] text-ink-muted font-mono">
            Leave a rate at 0 to fall back to the static amounts below (legacy manual mode).
          </div>
          <Num label="Life tax ₹ (static fallback)" value={form.life_tax} onChange={(v) => set("life_tax", v)} />
          <Num label="Insured value ₹ (static fallback)" value={form.insured_value} onChange={(v) => set("insured_value", v)} />
          <Num label="OD amount ₹ (static fallback)" value={form.od_amount} onChange={(v) => set("od_amount", v)} />
          <Num label="TP value ₹ (CC unknown)" value={form.tp_value} onChange={(v) => set("tp_value", v)} />
          <Num label="Nil-Dip ₹ (static fallback)" value={form.nil_dip} onChange={(v) => set("nil_dip", v)} />

          <SectionTitle title="Additional Accessories" />
          <Num label="Add. accessories amount" value={form.add_acc_amount} onChange={(v) => set("add_acc_amount", v)} />
          <Num label="Add. accessories margin %" value={form.add_acc_margin} onChange={(v) => set("add_acc_margin", v)} step="0.01" />

          <SectionTitle title="Quote Options (Petrol)" />
          <Num label="Full Accessories ₹ (quote default)" value={form.full_accessories_amount} onChange={(v) => set("full_accessories_amount", v)} />
          <Num label="5Y Extended Warranty ₹ (quote default)" value={form.ext_warranty_5y_amount} onChange={(v) => set("ext_warranty_5y_amount", v)} />
          <div className="lg:col-span-3 text-[10px] text-ink-muted font-mono">
            These auto-fill the FULL ACCESSORIES and 5Y EXTENDED WARRANTY lines on petrol quotes. Sales staff can still override per quote.
          </div>

          <SectionTitle title="AD (Sub-Dealer)" />
          <Num label="AD discount % of dealer margin" value={form.ad_discount_pct} onChange={(v) => set("ad_discount_pct", v)} step="0.01" />
          <Num label="AD discount ₹ (static fallback)" value={form.ad_discount} onChange={(v) => set("ad_discount", v)} />
          <Num label="AD mandatory acc landing" value={form.ad_mandatory_landing} onChange={(v) => set("ad_mandatory_landing", v)} />
          <Num label="Service charge" value={form.service_charge} onChange={(v) => set("service_charge", v)} />
          <Num label="Service GST %" value={form.service_gst_pct} onChange={(v) => set("service_gst_pct", v)} step="0.1" />
          <div className="lg:col-span-3 text-[10px] text-ink-muted font-mono">
            When AD discount % is set, the discount = dealer margin × % rounded to the nearest ₹100 and scales automatically with margin changes.
          </div>

          <SectionTitle title="GST + HSN (matches EMERGENT_REFERENCE)" />
          <Field label="Package HSN" value={form.package_hsn} onChange={(v) => set("package_hsn", v)} />
          <Field label="Helmet HSN" value={form.helmet_hsn} onChange={(v) => set("helmet_hsn", v)} />
          <Field label="Accessories HSN" value={form.accessories_hsn} onChange={(v) => set("accessories_hsn", v)} />
          <Num label="Package IGST %" value={form.package_igst_pct} onChange={(v) => set("package_igst_pct", v)} step="0.01" />
          <Num label="Package CGST %" value={form.package_cgst_pct} onChange={(v) => set("package_cgst_pct", v)} step="0.01" />
          <Num label="Company discount" value={form.company_discount} onChange={(v) => set("company_discount", v)} />
        </div>

        {preview && (
          <div className="mx-6 mb-4 border border-emerald-300 bg-emerald-50/40 p-4 text-xs" data-testid="ref-preview">
            <div className="font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-2">Live Preview</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="font-semibold mb-1">Retail (GENERAL)</div>
                {Object.entries(preview.retail).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono"><span className="text-ink-muted">{k}</span><span>{inr(v)}</span></div>
                ))}
              </div>
              <div>
                <div className="font-semibold mb-1">AD (Sub-Dealer)</div>
                {Object.entries(preview.ad).map(([k, v]) => (
                  <div key={k} className="flex justify-between font-mono"><span className="text-ink-muted">{k}</span><span>{inr(v)}</span></div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="border-t border-canvas-line px-6 py-3 flex justify-between">
          <button onClick={computePreview} data-testid="ref-preview-btn"
            className="px-4 py-2 border border-canvas-line text-sm hover:border-ink flex items-center gap-1">
            <Calculator size={12} weight="bold" /> Compute Preview
          </button>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-canvas-line text-sm hover:border-ink">Cancel</button>
            <button onClick={save} disabled={saving} data-testid="ref-save"
              className="px-4 py-2 bg-ink text-white text-sm font-semibold hover:bg-brand disabled:opacity-50">
              {saving ? "Saving…" : (isNew ? "Create Reference" : "Save Changes")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =============================================================
 * 3 + 4. MASTER (Retail / AD) - compute grid + publish
 * ============================================================= */
function MasterTab({ branchTypeCode }) {
  const [rows, setRows] = useState([]);
  const [computed, setComputed] = useState({});   // ref_id -> {retail, ad}
  const [publishBusy, setPublishBusy] = useState({});
  const isAd = branchTypeCode === "AD";

  const load = useCallback(async () => {
    try {
      // References are branch-agnostic (one row per model). The AD tab lists
      // the same references as retail and publishes with target=AD.
      // `active` is a pure filter flag — we list ALL non-deleted rows.
      const { data } = await api.get("/pricing/references", {
        params: isAd ? { status: "all" } : { branch_type_code: "GENERAL", status: "all" },
      });
      const refs = (data || []).filter((r) => !r.deleted_at);
      setRows(refs);
      // Compute all in parallel
      const computes = await Promise.all(
        (data || []).map(async (ref) => {
          try {
            const { data: c } = await api.post("/pricing/compute", { reference: ref });
            return [ref.id, c];
          } catch { return [ref.id, null]; }
        })
      );
      setComputed(Object.fromEntries(computes));
    } catch (e) { toast.error(formatApiError(e)); }
  }, [branchTypeCode]);
  useEffect(() => { load(); }, [load]);

  const publish = async (r) => {
    if (!window.confirm(`Publish "${r.package_name}" to packages master (${branchTypeCode})?`)) return;
    setPublishBusy((b) => ({ ...b, [r.id]: true }));
    try {
      const { data } = await api.post(`/pricing/references/${r.id}/publish`, null, { params: { target: branchTypeCode } });
      toast.success(`${data.action === "created" ? "Created" : "Updated"} — ${inr(data.price)}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPublishBusy((b) => ({ ...b, [r.id]: false })); }
  };

  const publishAll = async () => {
    if (!window.confirm(`Publish ALL ${rows.length} ${branchTypeCode} references to packages?`)) return;
    let ok = 0, fail = 0;
    for (const r of rows) {
      try {
        await api.post(`/pricing/references/${r.id}/publish`, null, { params: { target: branchTypeCode } });
        ok++;
      } catch (e) { fail++; toast.error(`${r.package_name}: ${formatApiError(e)}`); }
    }
    toast.success(`Published ${ok} packages${fail ? ` (${fail} failed)` : ""}`);
    load();
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs text-ink-muted">
          {rows.length} {branchTypeCode} reference(s). Prices are computed live — click Publish to push into <code>packages</code>.
        </div>
        {rows.length > 0 && (
          <button onClick={publishAll} data-testid="master-publish-all"
            className="px-3 py-2 bg-emerald-700 text-white text-xs font-mono uppercase tracking-wider hover:bg-emerald-800 flex items-center gap-1">
            <RocketLaunch size={12} weight="bold" /> Publish All
          </button>
        )}
      </div>

      <div className="border border-canvas-line overflow-x-auto max-h-[65vh] overflow-y-auto">
        <table className="w-full text-sm">
          <thead className="bg-canvas-panel border-b border-canvas-line sticky top-0">
            <tr>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Package</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Unit Cost</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Billing</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">{isAd ? "AD Ex-Show" : "Ex-Show"}</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">{isAd ? "Total (AD)" : "Life Tax"}</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">{isAd ? "" : "Insurance"}</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left font-semibold">On-Road ₹</th>
              <th className="px-3 py-2 font-mono text-[10px] uppercase text-left">Published</th>
              <th className="w-24"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={9} className="px-3 py-10 text-center text-ink-muted text-xs">
                No {branchTypeCode} references yet. Add rows on the "Package Reference" tab first.
              </td></tr>
            ) : rows.map((r) => {
              const c = computed[r.id];
              const rt = c?.retail || {};
              const ad = c?.ad || {};
              const onroad = isAd ? ad.ad_total : rt.dealer_price;
              return (
                <tr key={r.id} className="border-b border-canvas-line/50" data-testid={`master-row-${r.id}`}>
                  <td className="px-3 py-2 text-sm font-semibold">{r.package_name}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(r.unit_cost)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(rt.billing_price)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(isAd ? ad.ad_ex_showroom : rt.ex_showroom)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{inr(isAd ? ad.ad_total : rt.life_tax)}</td>
                  <td className="px-3 py-2 font-mono text-xs">{isAd ? "" : inr(rt.insurance)}</td>
                  <td className="px-3 py-2 font-mono text-sm font-semibold text-emerald-700">{inr(onroad)}</td>
                  <td className="px-3 py-2 font-mono text-[10px]">
                    {r.last_published_at ? (
                      <>
                        <div>{inr(r.last_published_price)}</div>
                        <div className="text-ink-muted">{new Date(r.last_published_at).toLocaleDateString()}</div>
                      </>
                    ) : <span className="text-ink-muted">Never</span>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button onClick={() => publish(r)} disabled={publishBusy[r.id]}
                      data-testid={`master-publish-${r.id}`}
                      className="px-2 py-1 border border-emerald-700 text-emerald-700 text-[10px] font-mono uppercase tracking-wider hover:bg-emerald-50 disabled:opacity-50">
                      {publishBusy[r.id] ? "…" : "Publish"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- shared field helpers ---------- */
function Field({ label, value, onChange, testId, placeholder, required }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}{required ? " *" : ""}</label>
      <input value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm" />
    </div>
  );
}
function Num({ label, value, onChange, testId, step = "0.01" }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}</label>
      <input type="number" step={step} value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : parseFloat(e.target.value))}
        data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm tabular-nums font-mono" />
    </div>
  );
}
function SelectField({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-muted mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full px-3 py-2 bg-canvas border border-canvas-line text-sm">
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
function SectionTitle({ title }) {
  return (
    <div className="lg:col-span-3 pt-3 pb-1 border-b border-canvas-line font-mono text-[10px] uppercase tracking-widest text-ink-muted">
      {title}
    </div>
  );
}
